import { FunctionComponent } from 'react'
import { useHistory } from 'react-router-dom'
import {
  CardContentGrid,
  Chip,
  CollapsibleCard,
  IconButton,
  Tooltip,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import { CardContentField } from './CardContentField'
import {
  ContractTypeAPIResponse,
  SiteRFPDetails,
} from '@black-bear-energy/black-bear-energy-common'
import { formatAsPercentage } from 'components/helpers/formatAsPercentage'
import { formatAsCurrency } from 'components/helpers/formatAsCurrency'
import { formatAsYears } from 'components/helpers/formatAsYears'
import { formatTableDate } from 'components/helpers/formatTableDate'
import { DriveFileMoveRounded } from '@mui/icons-material'

export const ContractsDetailsCard: FunctionComponent<
  ContractsDetailsCardProps
> = ({ siteRFPDetails, isFetching }) => {
  const history = useHistory()
  const formattedRent =
    siteRFPDetails?.rentPerMW != null
      ? `$${Math.round(siteRFPDetails.rentPerMW).toLocaleString('en')}/MW`
      : null
  const ppaEscalator =
    siteRFPDetails?.ppaEscalator != null
      ? siteRFPDetails.ppaEscalator * 100
      : null
  const leaseEscalator =
    siteRFPDetails?.leaseEscalator != null
      ? siteRFPDetails.leaseEscalator * 100
      : null

  const contractDisplay = getContractTypeDisplayName(
    siteRFPDetails?.contractType
  )

  const isIconDisabled = siteRFPDetails?.contractType == null

  const tooltipSlotProps = {
    popper: {
      modifiers: [{ name: 'offset', options: { offset: [0, -14] } }],
    },
  }

  return (
    <CollapsibleCard
      title="Contracts Details"
      titleAdornment={
        contractDisplay ? (
          <Chip
            sx={{ height: '24px', fontSize: '14px', fontWeight: 400 }}
            label={contractDisplay}
          />
        ) : undefined
      }
      actions={
        <Tooltip
          title={isIconDisabled ? 'Contracts Checklist not available' : ''}
          placement="bottom"
          slotProps={tooltipSlotProps}
        >
          <span>
            <IconButton
              disabled={isIconDisabled}
              onClick={() => {
                if (!siteRFPDetails) {
                  return
                }
                history.push(
                  `/document-exchange/projects/${siteRFPDetails.customerRFPId}/sites/${siteRFPDetails.id}/contracts`
                )
              }}
            >
              <DriveFileMoveRounded
                sx={{
                  color: isIconDisabled
                    ? rucksack.palette.action.disabled
                    : rucksack.palette.primary.main,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      {siteRFPDetails && !siteRFPDetails.contractType && (
        <Typography sx={{ fontStyle: 'italic' }}>
          Contract Type for this Site has not yet been determined. Contract
          specific details can be viewed once this has been assigned.
        </Typography>
      )}
      {siteRFPDetails?.contractType && (
        <CardContentGrid columns={4}>
          {(siteRFPDetails.contractType === ContractTypeAPIResponse.Lease ||
            siteRFPDetails.contractType ===
              ContractTypeAPIResponse.LeaseAndPPA) && (
            <CardContentField
              label="Rent"
              value={formattedRent}
              isFetching={isFetching}
            />
          )}
          {(siteRFPDetails.contractType ===
            ContractTypeAPIResponse.LeaseAndPPA ||
            siteRFPDetails.contractType === ContractTypeAPIResponse.PPA) && (
            <>
              <CardContentField
                label="PPA Term"
                value={formatAsYears(siteRFPDetails.ppaTerm)}
                isFetching={isFetching}
              />
              <CardContentField
                label="PPA Escalator"
                value={formatAsPercentage(ppaEscalator)}
                isFetching={isFetching}
              />
              <CardContentField
                label="PPA Rate"
                value={formatAsCurrency(siteRFPDetails.ppaRate)}
                isFetching={isFetching}
              />
            </>
          )}
          {(siteRFPDetails.contractType === ContractTypeAPIResponse.Lease ||
            siteRFPDetails.contractType ===
              ContractTypeAPIResponse.LeaseAndPPA) && (
            <>
              <CardContentField
                label="Rent Commencement Date"
                value={
                  siteRFPDetails.rentCommencementDate
                    ? formatTableDate(siteRFPDetails.rentCommencementDate)
                    : null
                }
                isFetching={isFetching}
              />
              <CardContentField
                label="Lease Term"
                value={formatAsYears(siteRFPDetails.leaseTerm)}
                isFetching={isFetching}
              />
              <CardContentField
                label="Lease Escalator"
                value={formatAsPercentage(leaseEscalator)}
                isFetching={isFetching}
              />
            </>
          )}
          {siteRFPDetails.contractType === ContractTypeAPIResponse.EPC && (
            <>
              {siteRFPDetails.offsetRate != null && (
                <CardContentField
                  label="Calculated Offset Rate"
                  value={formatAsCurrency(siteRFPDetails.offsetRate)}
                  isFetching={isFetching}
                />
              )}
              <CardContentField
                label="EPC Price"
                value={formatAsCurrency(siteRFPDetails.epcPrice)}
                isFetching={isFetching}
              />
              <CardContentField
                label="Turnkey Price"
                value={formatAsCurrency(siteRFPDetails.turnkeyPrice)}
                isFetching={isFetching}
              />
              <CardContentField
                label="O&M Contract Cost"
                value={formatAsCurrency(siteRFPDetails.omContractCost)}
                isFetching={isFetching}
              />
            </>
          )}
        </CardContentGrid>
      )}
    </CollapsibleCard>
  )
}

function getContractTypeDisplayName(
  contractType?: ContractTypeAPIResponse | null
): string | null {
  if (contractType == null || contractType === ContractTypeAPIResponse.ESA) {
    return null
  }

  switch (contractType) {
    case ContractTypeAPIResponse.EPC:
      return 'EPC'
    case ContractTypeAPIResponse.Lease:
      return 'Lease'
    case ContractTypeAPIResponse.LeaseAndPPA:
      return 'PPA + Lease'
    case ContractTypeAPIResponse.PPA:
      return 'PPA'
  }
}

export interface ContractsDetailsCardProps {
  siteRFPDetails?: SiteRFPDetails | null
  isFetching: boolean
}
